import type { OfflineDb } from './db'
import type { OutboxOp } from './types'
import { uuidv7 } from './uuid'

/**
 * Outbox operations (doc §4/§5): a durable, per-user queue of the
 * technician's own owned actions, drained in enqueue order with explicit
 * dependencies (photos before the record that references them).
 */

/** At-rest encryption (§9): the op's sensitive content (the write body, its
 * human label, its interpolation values, and attachment metadata) is encrypted into `enc` on disk while
 * every index/control column stays plaintext so queries and status updates
 * are untouched. Codec off → stored verbatim (legacy behavior). */
async function encodeOp(db: OfflineDb, op: OutboxOp): Promise<OutboxOp> {
  if (!db.codec) return op
  const enc = await db.codec.encrypt(
    JSON.stringify({
      payload: op.payload ?? null,
      label: op.label,
      labelValues: op.labelValues ?? null,
      attachment: op.attachment ?? null,
    }),
  )
  const stored: OutboxOp = { ...op, label: '', enc }
  delete stored.payload
  delete stored.labelValues
  delete stored.attachment
  return stored
}

/**
 * Decode an at-rest-encrypted op. When the content CANNOT be recovered — the
 * vault is locked (no codec) or the ciphertext is unreadable — the row is
 * returned marked `locked` and is NEVER treated as sendable.
 *
 * Audit RUN-1: this previously fell through to `return stored`, whose
 * `payload`/`attachment` were deleted at encode time. The sync engine then saw
 * a well-formed op with `payload === undefined` and omitted the body entirely,
 * so a request op was replayed with NO body — destroying the technician's
 * captured work on a permanent 4xx, or worse, applying a state transition with
 * server defaults. This is reachable on every ordinary sign-in, because the
 * runtime drains as soon as the user is set while the codec is installed later
 * by the separate unlock step.
 */
async function decodeOp(db: OfflineDb, stored: OutboxOp): Promise<OutboxOp> {
  if (!stored.enc) return stored
  if (!db.codec) return { ...stored, locked: true }
  try {
    const sensitive = JSON.parse(await db.codec.decrypt(stored.enc)) as {
      payload: unknown
      label: string
      labelValues: OutboxOp['labelValues']
      attachment: OutboxOp['attachment']
    }
    const op: OutboxOp = { ...stored, label: sensitive.label }
    delete op.enc
    if (sensitive.payload !== null && sensitive.payload !== undefined) op.payload = sensitive.payload
    if (sensitive.labelValues) op.labelValues = sensitive.labelValues
    if (sensitive.attachment) op.attachment = sensitive.attachment
    return op
  } catch {
    return { ...stored, locked: true }
  }
}

/** Decoding single-op getter for callers that re-fetch by id (sync engine). */
export async function getOp(db: OfflineDb, opId: string): Promise<OutboxOp | undefined> {
  const stored = await db.outbox.get(opId)
  return stored ? decodeOp(db, stored) : undefined
}

export interface EnqueueInput {
  opId: string
  userId: string
  companyId: string
  module: string
  label: string
  labelKey?: OutboxOp['labelKey']
  labelValues?: Record<string, string | number> | undefined
  kind?: 'request' | 'attachment-upload'
  endpoint: string
  method?: OutboxOp['method']
  payload?: unknown
  idempotencyKey?: string
  entityType: string
  entityId: string
  baseVersion?: number | undefined
  deps?: string[]
  attachment?: OutboxOp['attachment']
}

/**
 * Strictly increasing enqueue sequence.
 *
 * `seq` used to be a bare `Date.now()`, which is only as fine-grained as the
 * clock: two ops enqueued in the same millisecond got the SAME seq, and the
 * replay order then fell through to the `opId` tiebreak in `listOps`. That
 * tiebreak is a string comparison of UUIDs whose low bits are random — v4 in
 * the tests, and the random tail of a same-millisecond v7 in the runtime — so
 * two ops made in one millisecond replayed in a random order. The outbox's
 * contract is that operations replay in the order the user made them, and
 * `deps` only covers pairs with a declared causal link.
 *
 * This was the undiagnosed offline flake: sync.test.ts's "replays a versioned
 * edit under If-Match" enqueues two independent ops back to back and indexes
 * the resulting fetch calls positionally, so a same-millisecond collision
 * swapped them roughly half the time.
 *
 * Monotonic within the process, and still a wall-clock value (so it stays
 * comparable across sessions and sorts old ops before new ones).
 */
let lastSeq = 0
function nextSeq(): number {
  const now = Date.now()
  lastSeq = now > lastSeq ? now : lastSeq + 1
  return lastSeq
}

export async function enqueueOp(db: OfflineDb, input: EnqueueInput): Promise<OutboxOp> {
  const op: OutboxOp = {
    opId: input.opId,
    userId: input.userId,
    companyId: input.companyId,
    seq: nextSeq(),
    createdAt: new Date().toISOString(),
    module: input.module,
    label: input.label,
    ...(input.labelKey ? { labelKey: input.labelKey } : {}),
    ...(input.labelValues ? { labelValues: input.labelValues } : {}),
    kind: input.kind ?? 'request',
    endpoint: input.endpoint,
    method: input.method ?? 'POST',
    ...(input.payload !== undefined ? { payload: input.payload } : {}),
    idempotencyKey: input.idempotencyKey ?? input.opId,
    entityType: input.entityType,
    entityId: input.entityId,
    ...(input.baseVersion !== undefined ? { baseVersion: input.baseVersion } : {}),
    deps: input.deps ?? [],
    status: 'pending',
    attempts: 0,
    nextAttemptAt: 0,
    ...(input.attachment ? { attachment: input.attachment } : {}),
  }
  await db.outbox.add(await encodeOp(db, op))
  return op
}

export async function listOps(db: OfflineDb, userId: string): Promise<OutboxOp[]> {
  const stored = await db.outbox.where('userId').equals(userId).toArray()
  const ops = await Promise.all(stored.map((op) => decodeOp(db, op)))
  return ops.sort((a, b) => a.seq - b.seq || a.opId.localeCompare(b.opId))
}

export async function pendingCount(db: OfflineDb, userId: string): Promise<number> {
  return db.outbox.where('userId').equals(userId).count()
}

/** Ops queued against a record — drives the per-record "Pending sync" badge. */
export async function opsForEntity(db: OfflineDb, userId: string, entityId: string): Promise<OutboxOp[]> {
  const stored = await db.outbox.where('[userId+entityId]').equals([userId, entityId]).toArray()
  return Promise.all(stored.map((op) => decodeOp(db, op)))
}

/**
 * Next batch eligible to run: pending, past backoff, with every declared
 * dependency gone (applied ops are removed). An op whose dependency sits in
 * failed/conflict is surfaced as blocked instead — a permanently failed
 * dependency must never silently drop the child (doc §5).
 */
export async function runnableOps(db: OfflineDb, userId: string, now = Date.now()): Promise<OutboxOp[]> {
  const ops = await listOps(db, userId)
  const byId = new Map(ops.map((op) => [op.opId, op]))
  const runnable: OutboxOp[] = []
  for (const op of ops) {
    if (op.status !== 'pending' && op.status !== 'blocked') continue
    // Encrypted-but-undecodable ops are NOT runnable (audit RUN-1). Skipping
    // rather than failing is deliberate: the work is intact on disk and will
    // drain normally once the vault is unlocked. Failing here would discard it.
    if (op.locked) continue
    if (op.nextAttemptAt > now) continue
    const deps = op.deps.map((id) => byId.get(id)).filter((d): d is OutboxOp => !!d)
    const stuckDep = deps.find((d) => d.status === 'failed' || d.status === 'conflict')
    if (stuckDep) {
      if (op.status !== 'blocked') await db.outbox.update(op.opId, { status: 'blocked' })
      continue
    }
    if (deps.length > 0) continue // healthy deps still queued — wait our turn
    runnable.push(op)
  }
  return runnable
}

export async function removeOp(db: OfflineDb, opId: string): Promise<void> {
  await db.outbox.delete(opId)
}

/** The worker chose to discard a failed/conflicted op (their call — §7). */
export async function discardOp(db: OfflineDb, opId: string): Promise<void> {
  const op = await db.outbox.get(opId)
  if (!op) return
  await db.outbox.delete(opId)
  if (op.kind === 'attachment-upload') await db.blobs.delete(op.entityId)
  // Children of a discarded op unblock into pending; their own replay will
  // surface honest reference errors if the parent truly mattered.
  const dependents = await db.outbox.where('userId').equals(op.userId).toArray()
  for (const child of dependents) {
    if (child.deps.includes(opId)) {
      await db.outbox.update(child.opId, {
        deps: child.deps.filter((d) => d !== opId),
        status: 'pending',
      })
    }
  }
}

/** Manual retry from the sync center: clear the error state and backoff. */
export async function retryOp(db: OfflineDb, opId: string): Promise<void> {
  await db.outbox.update(opId, { status: 'pending', nextAttemptAt: 0, lastError: undefined, conflict: undefined })
}

/** §7 stale-conflict recovery for mutable edits: re-aim the op at the
 * server's CURRENT version — keep-mine passes the original payload,
 * merge passes a field-merged one — and requeue it. A fresh idempotency
 * key keeps the retried request independent of the conflicted attempt.
 * Payload changes go through the codec so at-rest encryption is preserved. */
export async function rebaseOp(
  db: OfflineDb,
  opId: string,
  input: { baseVersion: number; payload?: unknown },
): Promise<void> {
  const stored = await db.outbox.get(opId)
  if (!stored) return
  const op = await decodeOp(db, stored)
  const next: OutboxOp = {
    ...op,
    ...(input.payload !== undefined ? { payload: input.payload } : {}),
    baseVersion: input.baseVersion,
    idempotencyKey: uuidv7(),
    status: 'pending',
    attempts: 0,
    nextAttemptAt: 0,
  }
  delete next.lastError
  delete next.conflict
  await db.outbox.put(await encodeOp(db, next))
}
